import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Page } from '../../layout/Page';
import { useOrgName } from '../../layout';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { formatInZone, timeZoneAbbreviation } from '../../design/timeZone';
import { Alert, Button, Drawer, EmptyState, ErrorState, SeverityLabel, useToast } from '../../components/shared';
import adminAPI, { errorMessage } from '../../services/adminApi';
import { SEVERITIES, abbreviationIn, describeCategoryEdit, describeDiff, diffTaxonomy, formatDate, plural, reanalysisLabel, reanalysisNote, versionLabel } from './adminModel';
import { useAdminData } from './useAdminData';
import './admin.css';

const SUBTITLE =
  'The classifications the AI applies to each call. A call can carry any number of them. Changes are saved as a draft version and apply to new calls once published.';
const SAVE_DELAY_MS = 700;
const REANALYSIS_DAYS = 31;
const newId = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export function AdminTaxonomyPage() {
  const org = useOrgName();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const { data, loading, error, retry, reload } = useAdminData({ versions: adminAPI.getTaxonomyVersions, counts: () => adminAPI.getTaxonomyCounts(30) });
  const [draft, setDraft] = useState(null);
  const [saveState, setSaveState] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const location = useLocation();
  const [publishOpen, setPublishOpen] = useState(location.hash === '#publish');
  const [reanalyze, setReanalyze] = useState(false);
  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    if (!publishOpen) return undefined;
    let cancelled = false;
    adminAPI.getReanalysisEstimate(REANALYSIS_DAYS).then(
      (result) => !cancelled && setEstimate(result),
      () => !cancelled && setEstimate(null)
    );
    return () => {
      cancelled = true;
    };
  }, [publishOpen]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const saveTimer = useRef(null);

  const versions = useMemo(() => (data ? [...data.versions].sort((a, b) => a.versionNumber - b.versionNumber) : []), [data]);
  const active = versions.find((v) => v.status === 'Active') || null;
  const draftVersion = [...versions].reverse().find((v) => v.status === 'Draft') || null;
  const requested = params.get('version');
  const selectedVersion =
    versions.find((v) => v.id === requested || versionLabel(v) === requested || `v${v.versionNumber}` === requested) || draftVersion || active || versions[versions.length - 1] || null;
  const isDraft = selectedVersion?.status === 'Draft';

  useEffect(() => {
    setDraft(isDraft && selectedVersion ? JSON.parse(JSON.stringify(selectedVersion)) : null);
  }, [isDraft, selectedVersion]);

  const shown = draft && selectedVersion && draft.id === selectedVersion.id ? draft : selectedVersion;
  const base = shown ? versions.find((v) => v.id === shown.basedOnVersionId) || (shown.status === 'Draft' ? active : null) : null;
  const diff = useMemo(() => (shown && shown.status === 'Draft' ? diffTaxonomy(shown, base) : null), [shown, base]);

  const counts = useMemo(() => {
    const map = new Map();
    (data?.counts || []).forEach((c) => map.set(c.subcategoryId ? `${c.categoryId}|${c.subcategoryId}` : c.categoryId, c.callCount));
    return map;
  }, [data]);

  const persist = useCallback(
    (next) => {
      clearTimeout(saveTimer.current);
      setSaveState('pending');
      saveTimer.current = setTimeout(async () => {
        try {
          await adminAPI.updateTaxonomyDraft(next);
          setSaveState('saved');
        } catch (err) {
          setSaveState({ error: errorMessage(err) });
        }
      }, SAVE_DELAY_MS);
    },
    []
  );

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const change = (mutate) => {
    if (!draft) return;
    const next = JSON.parse(JSON.stringify(draft));
    mutate(next);
    setDraft(next);
    persist(next);
  };

  const flush = async () => {
    if (saveState === 'pending' && draft) {
      clearTimeout(saveTimer.current);
      await adminAPI.updateTaxonomyDraft(draft);
      setSaveState('saved');
    }
  };

  const editAsDraft = async () => {
    setBusy(true);
    try {
      const created = await adminAPI.createTaxonomyDraft({ copyFromVersionId: active ? active.id : null });
      await reload();
      setParams({ version: created.id }, { replace: true });
      toast(`Created draft ${versionLabel(created)} from ${versionLabel(active)}`);
    } catch (err) {
      toast(errorMessage(err), 'alert');
    } finally {
      setBusy(false);
    }
  };

  const startFromDefault = async () => {
    setBusy(true);
    try {
      await adminAPI.createTaxonomyDraft({ startFromDefault: true });
      await reload();
    } catch (err) {
      toast(errorMessage(err), 'alert');
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await flush();
      await adminAPI.activateTaxonomy(draft.id);
      let rerun = null;
      let rerunError = null;
      if (reanalyze && estimate) {
        try {
          rerun = await adminAPI.reanalyzeCalls(estimate.days, estimate.callCount);
        } catch (err) {
          rerunError = errorMessage(err);
        }
      }
      setPublishOpen(false);
      setReanalyze(false);
      setParams({}, { replace: true });
      await reload();
      if (rerunError) toast(`Published ${versionLabel(draft)}. Re-analysis didn't start: ${rerunError}`, 'alert');
      else toast(rerun ? `Published ${versionLabel(draft)}. Re-analysis started for ${plural(rerun.queued, 'call')}.` : `Published ${versionLabel(draft)}. New calls use it from now on.`);
    } catch (err) {
      toast(errorMessage(err), 'alert');
    } finally {
      setBusy(false);
    }
  };

  const exportColumns = [
    { key: 'category', header: 'Category' },
    { key: 'subcategory', header: 'Subcategory' },
    { key: 'severity', header: 'Default severity' },
    { key: 'definition', header: 'Definition' },
  ];
  const exportRows = (shown?.categories || []).flatMap((c) => {
    const severity = (SEVERITIES.find((s) => s.value === c.defaultSeverity) || {}).label || c.defaultSeverity;
    const rows = [{ category: c.name, subcategory: '', severity, definition: c.description }];
    (c.subcategories || []).forEach((s) => rows.push({ category: c.name, subcategory: s.name, severity, definition: s.description }));
    return rows;
  });

  const actions = (
    <>
      <Button onClick={() => setHistoryOpen(true)} disabled={!versions.length}>
        <Icon name="history" className="ico-sm" />
        Version history
      </Button>
      {draftVersion ? (
        <Button variant="primary" onClick={() => setPublishOpen(true)} disabled={busy}>
          Publish {versionLabel(draftVersion)}
        </Button>
      ) : (
        <Button variant="primary" onClick={editAsDraft} disabled={busy || !active}>
          Edit as a new draft
        </Button>
      )}
    </>
  );

  const page = (children) => (
    <Page title="Taxonomy" subtitle={SUBTITLE} actions={data ? actions : null} exportConfig={{ name: 'Taxonomy', columns: exportColumns, rows: exportRows }}>
      {children}
    </Page>
  );

  if (error) return page(<ErrorState title="Taxonomy couldn't load" body="Vertolytics couldn't read the taxonomy. Calls keep being classified with the active version." error={error} onRetry={retry} />);

  if (loading || !data) {
    return page(
      <div className="stack-lg">
        <section className="card is-loading">
          <div className="card-body">
            <div style={{ height: 34 }} />
          </div>
        </section>
        <div className="grid">
          <section className="card c-5 is-loading">
            <div className="card-body">
              <div style={{ height: 520 }} />
            </div>
          </section>
          <section className="card c-7 is-loading">
            <div className="card-body">
              <div style={{ height: 520 }} />
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (!versions.length) {
    return page(
      <EmptyState
        icon="tag"
        title="No taxonomy yet"
        body={`Start from the Vertolytics HOA taxonomy (16 categories, 6 call-level classifications) and edit it to fit ${org}.`}
        actions={
          <Button variant="primary" onClick={startFromDefault} disabled={busy}>
            Start from HOA taxonomy
          </Button>
        }
      />
    );
  }

  const categories = shown.categories || [];
  const topic = categories.filter((c) => (c.subcategories || []).length);
  const callLevel = categories.filter((c) => !(c.subcategories || []).length);
  const requestedCategory = params.get('category');
  const category =
    categories.find((c) => c.id === selectedCategory) ||
    (requestedCategory ? categories.find((c) => c.id === requestedCategory || c.name.toLowerCase() === requestedCategory.toLowerCase()) : null) ||
    categories[0] ||
    null;
  const shownVersions = versions.slice(-3);

  const caption = [
    active ? `${versionLabel(active)} has been active since ${formatDate(active.activatedAt || active.createdAt)}.` : 'No version is active yet; publish a draft to start classifying calls.',
    draftVersion && diff && shown.id === draftVersion.id ? `${versionLabel(draftVersion)} changes: ${describeDiff(diff)}.` : null,
  ]
    .filter(Boolean)
    .join(' ');

  const changeChip = (c) => {
    if (!diff) return null;
    if (diff.added.some((a) => a.id === c.id)) return <span className="chg add">New</span>;
    if (diff.edited.some((a) => a.id === c.id)) return <span className="chg ren">Edited</span>;
    return null;
  };

  const row = (c) => (
    <div
      className={`cat${category && c.id === category.id ? ' sel' : ''}`}
      key={c.id}
      role="button"
      tabIndex={0}
      onClick={() => setSelectedCategory(c.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') setSelectedCategory(c.id);
      }}
    >
      <span className="muted">
        <Icon name="grip" className="ico-sm" />
      </span>
      <span>{c.name}</span>
      {changeChip(c)}
      <span className="meta">
        <span>
          <SeverityLabel severity={c.defaultSeverity} />
        </span>
        <span className="num" style={{ width: 44, textAlign: 'right' }}>
          {counts.has(c.id) ? fmt.int(counts.get(c.id)) : '—'}
        </span>
      </span>
    </div>
  );

  const addCategory = () =>
    change((v) => {
      const id = newId();
      v.categories.push({ id, name: 'New Category', description: '', defaultSeverity: 'Standard', sortOrder: v.categories.length, isActive: true, subcategories: [] });
      setSelectedCategory(id);
    });

  return page(
    <div className="stack-lg">
      <section className="card">
        <div className="card-body row-wrap" style={{ gap: 16 }}>
          <div className="row" style={{ gap: 10 }}>
            <span className="small muted">Version</span>
            <div className="seg" role="group" aria-label="Version">
              {shownVersions.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  aria-pressed={v.id === shown.id ? 'true' : 'false'}
                  onClick={async () => {
                    await flush();
                    setSelectedCategory(null);
                    setParams({ version: v.id }, { replace: true });
                  }}
                >
                  {versionLabel(v)} {v.status}
                </button>
              ))}
            </div>
          </div>
          <span className="caption">{caption}</span>
        </div>
      </section>
      {saveState && saveState.error && <Alert tone="error" title="The draft couldn't be saved">{saveState.error}</Alert>}
      <div className="grid">
        <section className="card c-5">
          <div className="card-head" style={{ paddingBottom: 12 }}>
            <div>
              <div className="t">Categories</div>
              <div className="d">
                {plural(topic.length, 'topic category', 'topic categories')} and {plural(callLevel.length, 'call-level classification')}
              </div>
            </div>
            <div className="acts">
              <button className="btn btn-sm" type="button" onClick={addCategory} disabled={!draft} title={draft ? 'Add a category' : 'Edit as a new draft to add categories'}>
                <Icon name="plus" className="ico-sm" />
                Add
              </button>
            </div>
          </div>
          <div style={{ maxHeight: 640, overflow: 'auto' }}>
            <div className="caption" style={{ padding: '8px 12px' }}>
              Topic categories
            </div>
            {topic.map(row)}
            <div className="caption" style={{ padding: '14px 12px 8px', borderTop: '1px solid var(--divider)' }}>
              Call-level classifications (no subcategories)
            </div>
            {callLevel.map(row)}
          </div>
        </section>
        <section className="card c-7">
          <div className="card-head">
            <div>
              <div className="t">{category ? category.name : 'Edit category'}</div>
              <div className="d">
                {draft
                  ? `Changes save to the ${versionLabel(draft)} draft${saveState === 'pending' ? '…' : ''}`
                  : `${versionLabel(shown)} is ${shown.status.toLowerCase()} and can't be edited. Edit as a new draft to change it.`}
              </div>
            </div>
          </div>
          <div className="card-body stack">
            {category ? (
              <CategoryEditor
                key={`${shown.id}-${category.id}`}
                category={category}
                editable={Boolean(draft)}
                isNewSub={(sub) => Boolean(diff && diff.addedSubs.some((a) => a.sub.id === sub.id))}
                count={counts.get(category.id)}
                activeLabel={versionLabel(active || shown)}
                org={org}
                onChange={(mutate) =>
                  change((v) => {
                    const target = v.categories.find((c) => c.id === category.id);
                    if (target) mutate(target, v);
                  })
                }
                onRemove={() => {
                  change((v) => {
                    v.categories = v.categories.filter((c) => c.id !== category.id);
                  });
                  setSelectedCategory(null);
                }}
              />
            ) : (
              <p className="caption">Add a category to start.</p>
            )}
          </div>
        </section>
      </div>
      <Drawer
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        title={`Publish taxonomy ${versionLabel(draftVersion)}`}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setPublishOpen(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" type="button" onClick={publish} disabled={busy}>
              Publish {versionLabel(draftVersion)}
            </button>
          </>
        }
      >
        {draftVersion && (
          <PublishSummary
            draft={draft && draft.id === draftVersion.id ? draft : draftVersion}
            base={versions.find((v) => v.id === draftVersion.basedOnVersionId) || active}
            active={active}
            reanalyze={reanalyze}
            onReanalyze={setReanalyze}
            estimate={estimate}
          />
        )}
      </Drawer>
      <Drawer open={historyOpen} onClose={() => setHistoryOpen(false)} title="Version history">
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {[...versions].reverse().map((v) => (
            <li key={v.id} className="row" style={{ padding: '10px 0', borderTop: '1px solid var(--divider)', gap: 12 }}>
              <span className="num small strong" style={{ width: 36 }}>
                {versionLabel(v)}
              </span>
              <div className="grow">
                <div className="small">{v.name}</div>
                <div className="caption">
                  Created {formatDate(v.createdAt)}
                  {v.activatedAt ? ` · activated ${formatDate(v.activatedAt)}` : ''}
                  {v.retiredAt ? ` · retired ${formatDate(v.retiredAt)}` : ''}
                </div>
              </div>
              <span className={`badge${v.status === 'Active' ? ' b-good' : v.status === 'Draft' ? ' b-info' : ''}`}>{v.status}</span>
              <button
                className="btn btn-sm"
                type="button"
                onClick={() => {
                  setHistoryOpen(false);
                  setParams({ version: v.id }, { replace: true });
                }}
              >
                View
              </button>
            </li>
          ))}
        </ul>
      </Drawer>
    </div>
  );
}

function CategoryEditor({ category, editable, isNewSub, count, activeLabel, org, onChange, onRemove }) {
  const [newSub, setNewSub] = useState(null);
  const [newPhrase, setNewPhrase] = useState(null);
  const abbreviation = abbreviationIn(category.name);
  const subs = category.subcategories || [];
  const phrases = category.examplePhrases || [];

  return (
    <>
      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="cn">
            Name
          </label>
          <input className={`input${abbreviation ? ' invalid' : ''}`} id="cn" value={category.name} readOnly={!editable} onChange={(e) => onChange((c) => (c.name = e.target.value))} />
          {abbreviation ? (
            <span className="field-error">
              <Icon name="alert" className="ico-sm" />
              Use full names. Abbreviations like “{abbreviation}” aren't allowed in category names.
            </span>
          ) : (
            <span className="hint">Full words only; this name appears in reports and exports.</span>
          )}
        </div>
        <div className="field">
          <label className="label" htmlFor="cs">
            Default severity
          </label>
          <select className="select" id="cs" value={category.defaultSeverity} disabled={!editable} onChange={(e) => onChange((c) => (c.defaultSeverity = e.target.value))}>
            {SEVERITIES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <span className="hint">The model can raise severity for a specific call, with a quote as evidence.</span>
        </div>
      </div>
      <div className="field">
        <label className="label" htmlFor="cd">
          Definition for the AI
        </label>
        <textarea className="textarea" id="cd" rows={3} maxLength={200} value={category.description || ''} readOnly={!editable} onChange={(e) => onChange((c) => (c.description = e.target.value))} />
        <span className="hint">Write it the way you'd brief a new agent. Clear boundaries with similar categories improve accuracy.</span>
      </div>
      {subs.length || newSub != null ? (
        <div className="field">
          <span className="label">Subcategories</span>
          <div className="row-wrap">
            {subs.map((s) => (
              <span className="subchip" key={s.id}>
                {s.name}
                {isNewSub(s) && (
                  <>
                    {' '}
                    <span className="chg add">New</span>
                  </>
                )}
                {editable && (
                  <button className="btn btn-ghost btn-sm icon-btn" type="button" aria-label={`Remove ${s.name}`} onClick={() => onChange((c) => (c.subcategories = c.subcategories.filter((x) => x.id !== s.id)))}>
                    <Icon name="x" className="ico-sm" />
                  </button>
                )}
              </span>
            ))}
            {newSub != null && (
              <input
                className="input"
                style={{ maxWidth: 220, height: 32 }}
                autoFocus
                value={newSub}
                aria-label="New subcategory name"
                placeholder="Subcategory name"
                onChange={(e) => setNewSub(e.target.value)}
                onBlur={() => setNewSub(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setNewSub(null);
                  if (e.key === 'Enter' && newSub.trim()) {
                    const name = newSub.trim();
                    onChange((c) => c.subcategories.push({ id: newId(), name, description: '', sortOrder: c.subcategories.length, isActive: true }));
                    setNewSub(null);
                  }
                }}
              />
            )}
            {editable && (
              <button className="btn btn-sm" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setNewSub('')}>
                <Icon name="plus" className="ico-sm" />
                Add subcategory
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="alert">
          <Icon name="info" />
          <div>
            Call-level classifications have no subcategories. They sit alongside topic categories: a call can be both Maintenance Request and Complaint.
            {editable && (
              <>
                {' '}
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setNewSub('')}>
                  Add a subcategory
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <div className="field">
        <span className="label">
          Example phrases{' '}
          <span className="caption" style={{ fontWeight: 400 }}>
            used to brief the model
          </span>
        </span>
        <div className="stack" style={{ gap: 6 }}>
          {phrases.length ? (
            phrases.map((p, i) => (
              <div className="panel small row phrase" key={`${p}-${i}`} style={{ gap: 8 }}>
                <span className="grow">“{p}”</span>
                {editable && (
                  <button className="btn btn-ghost btn-sm icon-btn phrase-x" type="button" aria-label="Remove phrase" style={{ width: 20, height: 20, minHeight: 20, padding: 0 }} onClick={() => onChange((c) => (c.examplePhrases = (c.examplePhrases || []).filter((_, j) => j !== i)))}>
                    <Icon name="x" className="ico-sm" />
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="caption">No examples yet. Add 3 to 5 real phrases from {org} calls.</div>
          )}
          {editable && phrases.length < 5 && newPhrase == null && (
            <div>
              <button className="btn btn-sm" type="button" onClick={() => setNewPhrase('')}>
                <Icon name="plus" className="ico-sm" />
                Add phrase
              </button>
            </div>
          )}
          {editable && phrases.length < 5 && newPhrase != null && (
            <input
              autoFocus
              onBlur={() => !(newPhrase || '').trim() && setNewPhrase(null)}
              className="input"
              maxLength={80}
              value={newPhrase || ''}
              placeholder="Add a phrase a caller said, then press Enter"
              aria-label="Add an example phrase"
              onChange={(e) => setNewPhrase(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (newPhrase || '').trim()) {
                  const phrase = newPhrase.trim().replace(/^[“"]|[”"]$/g, '');
                  onChange((c) => (c.examplePhrases = [...(c.examplePhrases || []), phrase]));
                  setNewPhrase('');
                }
              }}
            />
          )}
        </div>
      </div>
      <div className="panel small row" style={{ gap: 10 }}>
        <Icon name="chart" />
        <span className="grow">
          In {activeLabel}: <strong>{plural(count || 0, 'call')}</strong> in the last 30 days.
        </span>
        <Link to={`/calls?categoryIds=${encodeURIComponent(category.id)}`}>View calls</Link>
      </div>
      {editable && (
        <div className="row">
          <button className="btn btn-sm btn-ghost" type="button" onClick={onRemove}>
            <Icon name="trash" className="ico-sm" />
            Remove category
          </button>
        </div>
      )}
    </>
  );
}

function PublishSummary({ draft, base, active, reanalyze, onReanalyze, estimate }) {
  const diff = diffTaxonomy(draft, base);
  const lines = [
    ...diff.added.map((c) => ['add', 'Added', `${c.name}${(c.subcategories || []).length ? ` (${plural(c.subcategories.length, 'subcategory', 'subcategories')})` : ''}`]),
    ...diff.addedSubs.map(({ category, sub }) => ['add', 'Added', `${category.name} › ${sub.name}`]),
    ...diff.edited
      .map((c) => [c, describeCategoryEdit((base?.categories || []).find((b) => b.id === c.id), c)])
      .filter(([, what]) => what)
      .map(([c, what]) => ['ren', 'Edited', `${c.name} ${what}`]),
    ...diff.removed.map((c) => ['del', 'Removed', c.name]),
    ...diff.removedSubs.map(({ category, sub }) => ['del', 'Removed', `${category.name} › ${sub.name}`]),
  ];
  return (
    <div className="stack">
      <p className="small">
        New calls will be classified with {versionLabel(draft)} starting now.
        {active ? ` Calls already analyzed keep their ${versionLabel(active)} classifications unless you re-analyze them.` : ''}
      </p>
      <div className="panel small stack" style={{ gap: 6 }}>
        {lines.length ? (
          lines.map(([cls, label, text], i) => (
            <div className="row" style={{ gap: 8 }} key={`${label}-${text}-${i}`}>
              <span className={`chg ${cls}`}>{label}</span>
              {text}
            </div>
          ))
        ) : (
          <span className="caption">No changes from {versionLabel(base) || 'the previous version'}.</span>
        )}
      </div>
      {active && (
        <>
          <label className="check">
            <input type="checkbox" checked={reanalyze} onChange={(e) => onReanalyze(e.target.checked)} disabled={!estimate || estimate.callCount === 0 || Boolean(estimate.nextAllowedAt)} />
            {reanalysisLabel(estimate)}
          </label>
          <p className="caption">
            {estimate?.nextAllowedAt
              ? `Calls were re-analyzed in the last 24 hours. Re-analysis is available again after ${formatInZone(new Date(estimate.nextAllowedAt), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ${timeZoneAbbreviation()}.`
              : `${reanalysisNote(estimate)}${estimate?.alreadyCurrentCount ? ` ${plural(estimate.alreadyCurrentCount, 'call')} already use the current version and are skipped.` : ''}`}
          </p>
        </>
      )}
    </div>
  );
}
